import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Account {
  id: string
  platform: string
  username: string
  display_name: string
  followers: number
  following: number
  posts_count: number
  engagement_rate: number
  is_verified: boolean
  is_connected: boolean
}

interface Post {
  id: string
  platform: string
  account_name: string
  content: string
  post_type: string
  status: string
  likes: number
  comments: number
  shares: number
  scheduled_at: string | null
  published_at: string | null
}

export default function SocialMediaPage() {
  const [accounts, setAccounts] = useState<Account[]>([])
  const [posts, setPosts] = useState<Post[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [accountsRes, postsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/social-media/accounts'),
      fetch(API_BASE + '/social-media/posts'),
      fetch(API_BASE + '/social-media/statistics')
    ])
    if (accountsRes.ok) setAccounts((await accountsRes.json()).accounts || [])
    if (postsRes.ok) setPosts((await postsRes.json()).posts || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800'
      case 'scheduled': return 'bg-blue-100 text-blue-800'
      case 'draft': return 'bg-gray-100 text-gray-600'
      case 'failed': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlatformColor = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter': return 'bg-blue-400 text-white'
      case 'facebook': return 'bg-blue-600 text-white'
      case 'instagram': return 'bg-pink-500 text-white'
      case 'linkedin': return 'bg-blue-700 text-white'
      case 'tiktok': return 'bg-black text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Social Media Management</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Create Post</button>
            <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Connect Account</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Followers</div>
              <div className="text-2xl font-bold">{formatNumber(statistics.total_followers)}</div>
              <div className="text-xs text-green-600">+{statistics.followers_growth}% this month</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Posts This Month</div>
              <div className="text-2xl font-bold">{statistics.posts_this_month}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Engagement</div>
              <div className="text-2xl font-bold">{formatNumber(statistics.total_engagement)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Engagement Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.avg_engagement_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {accounts.map(account => (
            <div key={account.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-2 py-1 rounded text-xs font-medium ${getPlatformColor(account.platform)}`}>
                  {account.platform}
                </span>
                {account.is_verified && <span className="text-blue-500">✓</span>}
              </div>
              <div className="font-semibold">{account.display_name}</div>
              <div className="text-sm text-gray-500">@{account.username}</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div>
                  <div className="font-bold">{formatNumber(account.followers)}</div>
                  <div className="text-xs text-gray-500">Followers</div>
                </div>
                <div>
                  <div className="font-bold">{formatNumber(account.posts_count)}</div>
                  <div className="text-xs text-gray-500">Posts</div>
                </div>
                <div>
                  <div className="font-bold text-green-600">{account.engagement_rate}%</div>
                  <div className="text-xs text-gray-500">Eng.</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Posts</h2></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {posts.slice(0, 10).map(post => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm max-w-md truncate">{post.content}</div>
                    <div className="text-xs text-gray-500">{post.account_name} • {post.post_type}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${getPlatformColor(post.platform)}`}>
                      {post.platform}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3 text-sm">
                      <span title="Likes">❤️ {formatNumber(post.likes)}</span>
                      <span title="Comments">💬 {formatNumber(post.comments)}</span>
                      <span title="Shares">🔄 {formatNumber(post.shares)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(post.status)}`}>
                      {post.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
